import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { House, User, SignOut, Storefront, CalendarBlank } from "@phosphor-icons/react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-neutral-200"
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <div className="w-7 h-7 border border-neutral-900 flex items-center justify-center">
              <div className="w-2 h-2 bg-neutral-900" />
            </div>
            <span className="font-display font-semibold tracking-tight text-lg">DenShare</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm">
            <NavLink data-testid="nav-browse" to="/browse" className={({isActive}) =>
              `hover:text-neutral-900 transition-colors ${isActive ? "text-neutral-900" : "text-neutral-500"}`
            }>Browse</NavLink>
            {user && (
              <>
                <NavLink data-testid="nav-host" to="/host" className={({isActive}) =>
                  `hover:text-neutral-900 transition-colors ${isActive ? "text-neutral-900" : "text-neutral-500"}`
                }>Host</NavLink>
                <NavLink data-testid="nav-trips" to="/trips" className={({isActive}) =>
                  `hover:text-neutral-900 transition-colors ${isActive ? "text-neutral-900" : "text-neutral-500"}`
                }>Trips</NavLink>
              </>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {!user ? (
              <>
                <Button
                  data-testid="nav-login-btn"
                  variant="ghost"
                  className="text-sm h-9"
                  onClick={() => nav("/login")}
                >Sign in</Button>
                <Button
                  data-testid="nav-signup-btn"
                  className="text-sm h-9 rounded-md bg-neutral-900 hover:bg-neutral-800"
                  onClick={() => nav("/signup")}
                >Get started</Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-btn" className="flex items-center gap-2 h-9 px-2 border border-neutral-200 rounded-md hover:bg-neutral-50 transition-colors">
                    <div className="w-6 h-6 rounded-full bg-neutral-900 text-white text-xs flex items-center justify-center font-medium">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <span className="text-sm">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem data-testid="menu-browse" onClick={() => nav("/browse")}>
                    <House size={16} className="mr-2"/> Browse listings
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-host" onClick={() => nav("/host")}>
                    <Storefront size={16} className="mr-2"/> Host dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-trips" onClick={() => nav("/trips")}>
                    <CalendarBlank size={16} className="mr-2"/> My trips
                  </DropdownMenuItem>
                  <DropdownMenuSeparator/>
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}>
                    <SignOut size={16} className="mr-2"/> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-neutral-200 mt-24">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row justify-between gap-6 text-sm text-neutral-500">
          <div>
            <div className="font-display font-semibold text-neutral-900 mb-1">DenShare</div>
            <div>Workspaces by the hour — hosted from real homes.</div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <NavLink to="/terms" data-testid="footer-terms" className="hover:text-neutral-900">Terms</NavLink>
            <NavLink to="/privacy" data-testid="footer-privacy" className="hover:text-neutral-900">Privacy</NavLink>
            <NavLink to="/cancellation" data-testid="footer-cancellation" className="hover:text-neutral-900">Cancellation</NavLink>
            <span>© {new Date().getFullYear()} DenShare · A quieter way to work</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
